/**
 * GlobalAutoSync Component
 * 
 * Komponen ini berjalan secara global di seluruh aplikasi untuk
 * memastikan pembayaran offline tersinkron otomatis ke server
 * ketika petugas terhubung ke internet.
 * 
 * Fitur:
 * - Auto-sync setiap 30 detik ketika online (SELALU AKTIF untuk Petugas)
 * - Instant sync ketika koneksi internet terdeteksi
 * - Tidak bisa dimatikan oleh petugas
 * - Bekerja di semua halaman aplikasi
 */

import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Zap, Wifi } from "lucide-react";
import {
  getPendingOfflinePayments,
  markPaymentSynced,
} from "@/utils/offlineDB";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-sync interval in milliseconds (30 seconds)
const AUTO_SYNC_INTERVAL = 30000;

function getAuthHeader() {
  const token = localStorage.getItem("user_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function GlobalAutoSync() {
  const autoSyncIntervalRef = useRef(null);
  const isSyncingRef = useRef(false);

  // Check if user is petugas (Auto-sync ALWAYS active for petugas)
  const isPetugasLoggedIn = useCallback(() => {
    const userRole = localStorage.getItem("user_role") || "";
    const userToken = localStorage.getItem("user_token");
    
    return userToken && userRole.toLowerCase() === "petugas";
  }, []);

  // Perform auto-sync
  const performAutoSync = useCallback(async (silent = true) => {
    // Check if user is petugas
    if (!isPetugasLoggedIn()) {
      return;
    }

    // Prevent concurrent sync operations
    if (isSyncingRef.current) {
      console.log('[GlobalAutoSync] Sync already in progress, skipping...');
      return;
    }

    // Check if online
    if (!navigator.onLine) {
      console.log('[GlobalAutoSync] Offline, skipping...');
      return;
    }

    // Get pending payments
    const pending = await getPendingOfflinePayments();
    if (pending.length === 0) {
      return;
    }

    console.log(`[GlobalAutoSync] Found ${pending.length} pending payments, syncing...`);
    isSyncingRef.current = true;

    try {
      // Format payments for sync
      const paymentsToSync = pending.map(p => ({
        offline_id: p.id,
        bill_ids: p.bill_ids,
        customer_no: p.customer_no,
        customer_name: p.customer_name,
        customer_address: p.customer_address || "",
        amount: p.amount,
        admin_fee: p.admin_fee,
        total: p.total,
        bill_details: p.bill_details || [],
        payment_method: p.payment_method || "",
        created_at: p.created_at
      }));

      const response = await axios.post(
        `${API}/user/offline/sync-payments`,
        paymentsToSync,
        { headers: getAuthHeader() }
      );

      const result = response.data;

      // Mark synced payments
      for (const synced of result.synced) {
        await markPaymentSynced(synced.offline_id);
      }

      // Update balance in localStorage
      if (result.balance_after !== undefined) {
        localStorage.setItem("user_balance", result.balance_after);
      }

      // Calculate total amount synced
      const totalAmountSynced = pending
        .filter(p => result.synced.some(s => s.offline_id === p.id))
        .reduce((sum, p) => sum + p.total, 0);

      // Show toast notification
      if (result.synced.length > 0 && !silent) {
        const formattedAmount = new Intl.NumberFormat("id-ID", { 
          style: "currency", 
          currency: "IDR", 
          minimumFractionDigits: 0 
        }).format(totalAmountSynced);
        
        toast.success(
          `Auto-Sync: ${result.synced.length} pembayaran (${formattedAmount}) berhasil disinkronkan`,
          { icon: <Zap className="w-4 h-4 text-yellow-500" /> }
        );
      }

      if (result.failed.length > 0) {
        toast.error(`Auto-Sync: ${result.failed.length} pembayaran gagal`);
      }

      console.log(`[GlobalAutoSync] Synced ${result.synced.length}, failed ${result.failed.length}`);

    } catch (error) {
      console.error('[GlobalAutoSync] Error:', error);
      // Don't show toast for every auto-sync error to avoid spam
    } finally {
      isSyncingRef.current = false;
    }
  }, [isPetugasLoggedIn]);

  // Start/stop auto-sync polling based on user role
  useEffect(() => {
    const startPolling = () => {
      if (!isPetugasLoggedIn()) {
        return;
      }

      console.log('[GlobalAutoSync] Auto-Sync AKTIF (tidak bisa dimatikan)');
      
      // Perform initial sync check (silent)
      performAutoSync(true);

      // Set up interval for periodic sync
      if (!autoSyncIntervalRef.current) {
        autoSyncIntervalRef.current = setInterval(() => {
          performAutoSync(true);
        }, AUTO_SYNC_INTERVAL);
      }
    };

    const stopPolling = () => {
      if (autoSyncIntervalRef.current) {
        console.log('[GlobalAutoSync] Stopping polling (user logged out or not petugas)');
        clearInterval(autoSyncIntervalRef.current);
        autoSyncIntervalRef.current = null;
      }
    };

    // Check and start/stop on mount
    if (isPetugasLoggedIn()) {
      startPolling();
    } else {
      stopPolling();
    }

    // Check periodically for login/logout changes
    const checkInterval = setInterval(() => {
      if (isPetugasLoggedIn() && !autoSyncIntervalRef.current) {
        startPolling();
      } else if (!isPetugasLoggedIn() && autoSyncIntervalRef.current) {
        stopPolling();
      }
    }, 5000);

    return () => {
      stopPolling();
      clearInterval(checkInterval);
    };
  }, [isPetugasLoggedIn, performAutoSync]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      if (!isPetugasLoggedIn()) return;

      // Check if there are pending payments
      const pending = await getPendingOfflinePayments();
      if (pending.length > 0) {
        toast.info(
          `Koneksi internet terdeteksi. Menyinkronkan ${pending.length} pembayaran...`,
          { icon: <Wifi className="w-4 h-4 text-green-500" /> }
        );
        
        // Small delay to ensure network is stable
        setTimeout(() => {
          performAutoSync(false); // Not silent - show result
        }, 2000);
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [isPetugasLoggedIn, performAutoSync]);

  // This component doesn't render anything
  return null;
}
